import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { computed, action } from 'mobx';
import * as classNames from 'classnames';
import { IAppOnlyStore } from 'stores/AllStores';
import { EContactSavingError } from 'stores/LeadContacts';
import { LeadContactView, LeadContactEditOrCreate } from './Contact';
import { IconButton } from 'components/action';
import { SavingIndicator, SavingIndicatorStatus } from 'components/indicator';
import { scrollWindow } from 'events/scroll';

// Enumerables
export enum EContactFieldName {
  firstName = 'firstName',
  middleName = 'middleName',
  lastName = 'lastName',
  title = 'title',
  emailAddress = 'emailAddress',
  officePhone = 'officePhone',
  mobilePhone = 'mobilePhone',
  fax = 'fax',
  default = 'default',
}

// Interfaces
export interface IContactField {
  name: EContactFieldName;
  label: string;
}

// Styles
const s = require('./LeadContacts.css');
const addIcon = require('assets/plus icon.svg');
const cancelIcon = require('assets/close orange icon.svg');

// Component
@inject('store')
@observer
export class LeadContacts extends React.Component<IAppOnlyStore, {}> {
  constructor(props: IAppOnlyStore) {
    super(props);
  }

  @computed private get createButton(): {
    onClick: () => void;
    label: string;
    icon: string;
  } {
    const { leadContacts } = this.props.store;

    if (leadContacts.creating && leadContacts.createContact !== null) {
      return {
        onClick: leadContacts.cancelCreatingNewContact,
        label: 'Cancel creating',
        icon: cancelIcon,
      };
    } else {
      return {
        onClick: this.handleStartContactCreating,
        label: 'Create contact',
        icon: addIcon,
      };
    }
  }

  @computed private get creatingOnly(): boolean {
    const { leadContacts } = this.props.store;
    return leadContacts.creating === true && leadContacts.editing === false;
  }

  private fields: IContactField[] = [
    { name: EContactFieldName.firstName, label: 'First name (required)' },
    { name: EContactFieldName.middleName, label: 'Middle name' },
    { name: EContactFieldName.lastName, label: 'Last name (required)' },
    { name: EContactFieldName.title, label: 'Title' },
    { name: EContactFieldName.emailAddress, label: 'Email address' },
    { name: EContactFieldName.officePhone, label: 'Office phone' },
    { name: EContactFieldName.mobilePhone, label: 'Mobile phone' },
    { name: EContactFieldName.fax, label: 'Fax' },
  ];

  public render() {
    const {
      saving,
      errorSaving,
      editing,
      creating,
      combined,
      createContact,
      editedContacts,
    } = this.props.store.leadContacts;

    return (
      <div className={s.list} ref={this.referenceList}>
        {(combined.length > 0 && (
          <div className={s.results}>
            {combined.map((contact, i) => {
              const key = `contact-${contact.id}`;
              return editing ? (
                <LeadContactEditOrCreate
                  key={`${key}-edit`}
                  order={i + 1}
                  textFields={this.fields}
                  contact={editedContacts[i]}
                />
              ) : (
                <LeadContactView key={key} contact={contact} />
              );
            })}
          </div>
        )) || (
          <div className={s.none}>
            <p>{this.props.store.lead.leadTitle} has no contacts defined</p>
          </div>
        )}

        {editing && (
          <div className={s.details}>
            <div>
              <button
                className={classNames('boxButton')}
                onClick={this.handleSave}
              >
                Save changes
              </button>
            </div>
            <div>
              <SavingIndicator
                labels={{
                  saving: 'Saving contacts...',
                  error:
                    errorSaving === EContactSavingError.NoName
                      ? 'Name is required'
                      : 'Could not save, try again',
                }}
                status={
                  saving === true && errorSaving === EContactSavingError.None
                    ? SavingIndicatorStatus.Saving
                    : errorSaving !== EContactSavingError.None
                    ? SavingIndicatorStatus.Error
                    : SavingIndicatorStatus.None
                }
              />
            </div>
          </div>
        )}

        <div ref={this.referenceCreateContactSection}>
          <IconButton
            className={s.createContact}
            labelClassName={s.createContactLabel}
            onClick={this.createButton.onClick}
            labelOnRight={true}
            label={this.createButton.label}
            showLabel={true}
            icon={this.createButton.icon}
          />

          {creating && (
            <LeadContactEditOrCreate
              key="creating-user"
              textFields={this.fields}
              contact={createContact}
            />
          )}
        </div>

        {creating && (
          <div className={s.details}>
            <div>
              <button
                className={classNames('boxButton')}
                onClick={this.handleCreate}
              >
                Create contact
              </button>
            </div>
            <div>
              <SavingIndicator
                labels={{
                  saving: 'Creating contact...',
                  error:
                    errorSaving === EContactSavingError.NoName
                      ? 'Name is required'
                      : 'Could not create, try again',
                }}
                status={
                  saving === true && errorSaving === EContactSavingError.None
                    ? SavingIndicatorStatus.Saving
                    : errorSaving !== EContactSavingError.None
                    ? SavingIndicatorStatus.Error
                    : SavingIndicatorStatus.None
                }
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  private list: HTMLDivElement = null;
  private referenceList = (ref: HTMLDivElement) => (this.list = ref);

  private createContactSection: HTMLDivElement = null;
  private referenceCreateContactSection = (ref: HTMLDivElement) =>
    (this.createContactSection = ref);

  private handleStartContactCreating = () => {
    if (this.createContactSection) {
      scrollWindow.to(this.createContactSection);
    }

    this.props.store.leadContacts.startCreatingNewContact();
  };

  @action private handleCreate = async (): Promise<any> => {
    const create = await this.props.store.leadContacts.saveNewContact();

    if (create) {
      scrollWindow.toY(0);
    }
  };

  @action private handleSave = async (): Promise<any> => {
    const complete = await this.props.store.leadContacts.saveEditedContacts();

    if (complete) {
      scrollWindow.toY(0);
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/sales/Lead/Contacts/LeadContacts.tsx